<template>
    <div>
        <div class="tw-flex tw-justify-center tw-my-2">
            <div class="tw-w-full md:tw-w-2/3">
                <h3 class="tw-text-white">{{ __('purchase.how_many_employees') }}</h3>
            </div>
        </div>
        <div class="tw-flex tw-justify-center tw-my-2">
            <div class="tw-w-full md:tw-w-2/3">
                <p class="tw-text-white">{{ __('purchase.include_yourself_text') }}</p>
            </div>
        </div>
        <form @submit="formWillSubmit">
            <div class="tw-flex tw-justify-center">
                <div class="tw-w-full md:tw-w-2/3 tw-table">
                    <div class="tw-w-3/4 tw-p-0 tw-table-cell tw-align-bottom">
                        <input 
                            type="text"
                            class="tw-w-full tw-px-3 tw-py-2 tw-border tw-text-white tw-border-gray-300 tw-bg-transparent tw-rounded"
                            :placeholder="__('purchase.num_employees_placeholder')"
                            v-model="numLicenses"
                            :class="{ 'tw-border-red-500': errors.license_quantity }"
                            @blur="updateInvoice" 
                        >
                    </div>

                    <div class="tw-w-1/4 tw-table-cell tw-align-bottom">
                        <div class="tw-px-3 tw-py-2">
                            <p class="tw-text-white tw-font-bold">{{ __('purchase.licenses') }}</p>
                        </div>
                    </div>
                    <div class="tw-table-row tw-mt-1">
                        <p v-show="errors.license_quantity" class="tw-text-red-600">{{ errors.license_quantity }}</p>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { onMounted, getCurrentInstance } from "vue";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";

interface Props {
    currentNumLicenses?: number | string;
    minimumLicenseCount?: number;
}

const props = withDefaults(defineProps<Props>(), {
    currentNumLicenses: 0,
    minimumLicenseCount: 3,
});

const emit = defineEmits<{
    "quantity-did-update": [numLicenses: number, arg2?: boolean, arg3?: boolean];
}>();

const instance = getCurrentInstance();
const __ = instance?.appContext.config.globalProperties.__;

// Create validation schema
const validationSchema = yup.object({
    license_quantity: yup
        .number()
        .required(__('purchase.validation.required', { field: __('purchase.field_names.num_licenses') }))
        .min(props.minimumLicenseCount, __('purchase.validation.min_value', { field: __('purchase.field_names.num_licenses'), min: props.minimumLicenseCount }))
        .integer(__('purchase.validation.numeric', { field: __('purchase.field_names.num_licenses') }))
        .typeError(__('purchase.validation.type_error_number', { field: __('purchase.field_names.num_licenses') })),
});

// Setup form validation
const { handleSubmit, errors } = useForm({
    validationSchema,
    validateOnMount: false,
});

// Use field for license quantity
const { value: numLicenses } = useField<number>("license_quantity");

// Initialize value on mount
onMounted(() => {
    // Convert string to number or default to null if empty
    const currentValue = props.currentNumLicenses;
    numLicenses.value = (currentValue === "" || currentValue === null) 
        ? undefined 
        : Number(currentValue);
});

// Form submission handler
const formWillSubmit = handleSubmit((values) => {
    emit("quantity-did-update", values.license_quantity, true, true);
}, () => {
    // Handle validation errors if needed
    console.log("Validation failed");
});

// Update invoice handler
const updateInvoice = async () => {
    const isValid = await handleSubmit((values) => {
        emit("quantity-did-update", values.license_quantity, false);
        return true;
    }, () => {
        return false;
    })();
};

// Expose methods for parent component access
defineExpose({
    formWillSubmit,
    updateInvoice,
});</script>

